import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import SubscriptionSuspended from '../pages/SubscriptionSuspended';

const ProtectedRoute = ({ children, allowedRoles, allowWhenBlocked = false }) => {
  const { user, loading } = useAuth();
  const { isBlocked, loading: subLoading } = useSubscription();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="protected-route-loader">
        <div className="text-center">
          <div className="inline-flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-md shadow-sm">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#002FA7] border-t-transparent"></div>
            <span className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Work Sans, sans-serif' }}>CivilX TMS</span>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    const next = `${location.pathname}${location.search}`;
    const skip = !next || next === '/' || next === '/dashboard' || next.startsWith('/verify-required');
    return <Navigate to={skip ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />;
  }

  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  if (!user.must_change_password && location.pathname === '/change-password') {
    return <Navigate to="/dashboard" replace />;
  }
  const needsVerification = user.email_verification_required && !user.email_verified;
  if (needsVerification && location.pathname !== '/verify-required') {
    return <Navigate to="/verify-required" replace />;
  }
  if (!needsVerification && location.pathname === '/verify-required') {
    return <Navigate to="/dashboard" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Hard lockout: company subscription suspended/cancelled blocks every route
  // for non-super-admins. The dedicated /suspended route opts out via
  // allowWhenBlocked so users can still see the explanation page + logout.
  if (
    user.role !== 'super_admin' &&
    !subLoading &&
    isBlocked &&
    !allowWhenBlocked
  ) {
    return <SubscriptionSuspended />;
  }

  return children;
};

export default ProtectedRoute;